import {
	KioskInfoType,
	PaymentRequestInfoType,
	StoreInfoType,
} from '@kinderlabs-pos/shared-data-type';
import { sleep } from '@kinderlabs-pos/shared-util';
import { OrderState, PaymentState } from '@kinderlabs-pos/state';
import { FullSizeCircularProgress } from '@kinderlabs-pos/ui-components';
import { Box, Button, Stack, Typography, styled } from '@mui/material';
import icCardImage from 'assets/ic-card.gif';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { KioskPaymentMethodType } from '../../KioskMainPageType';
import { useKioskTranslation } from 'apps/kiosk-main/src/const/useKioskTranslation';

const RETRY_LIMIT = 3;
export const KioskCardPaymentSection = ({
	goBack,
	storeInfo,
	kioskInfo,
	paymentMethod,
	handleSubmitPayment,
}: {
	goBack: () => void;
	storeInfo: StoreInfoType;
	kioskInfo: KioskInfoType;
	paymentMethod: Extract<KioskPaymentMethodType, 'CARD' | 'VIRTUAL_CARD'>;
	handleSubmitPayment: (paymentRequestInfo: PaymentRequestInfoType) => Promise<void>;
}) => {
	const { totalToPay } = useAtomValue(OrderState.aggregate.cart);
	const { t, kioskI18N } = useKioskTranslation();
	const { handleSubmit, isSubmitting, isValid, values, handleChange, setValues, errors } =
		PaymentState.actions.submit.usePaymentFormik({
			storeId: storeInfo.id,
			posId: kioskInfo.id,
			onClose: () => {},
			initialValues: {
				method: paymentMethod,
			},
			validationSchema: undefined,
			handleSubmitPayment,
			getHandlePaymentArgs: (values) => ({
				method: values.method,
				installment: 0,
				amount: totalToPay,
			}),
		});

	const paymentPM강제취소 = PaymentState.actions.submit.useHandlePaymentPM강제취소();
	// const { 이미꽂힌카드가있는지, 이미꽂혀있는카드정보검사하기 } = use이미꽂힌카드가있는지확인한다();
	// useEffect(() => {
	// 	if (이미꽂힌카드가있는지 === 'NO') {
	// 		(async () => {
	// 			handleSubmit();
	// 		})();
	// 	}

	// 	return () => {
	// 		paymentPM강제취소();
	// 	};
	// }, [이미꽂힌카드가있는지]);

	const [safeRunningCheck, setSafeRunningCheck] = useState(false);
	useEffect(() => {
		(async () => {
			try {
				setSafeRunningCheck(true);
				await handleSubmit();
			} finally {
				setTimeout(() => setSafeRunningCheck(false), 1200);
			}
		})();

		return () => {
			paymentPM강제취소();
		};
	}, []);

	const { isServerError, isRetrying } = useRetryFailedPayment({
		handleSubmitPayment,
		failedPaymentRequestData: values.failedPaymentRequestData,
		handleCompleteRefund중복결제: () => {
			setValues({ ...values, failedPaymentRequestData: undefined });
			goBack();
		},
	});

	return (
		<Stack flex={1}>
			<Stack alignItems={'center'}>
				<Typography variant='h5'>{t('payment.cardTitle')}</Typography>
			</Stack>
			{isServerError &&
				(isRetrying ? (
					<FullSizeCircularProgress />
				) : (
					<Stack
						flex={1}
						alignItems={'center'}
						justifyContent={'center'}
						width={'100%'}>
						<FullSizeCircularProgress />
						<Typography variant={'subtitle1'}>서버 에러입니다.</Typography>
						<Typography variant={'subtitle1'}>결제를 취소하고 돌아갑니다.</Typography>
					</Stack>
				))}
			{/* {이미꽂힌카드가있는지 && (
				<Stack
					flex={1}
					alignItems={'center'}
					justifyContent={'center'}
					width={'100%'}>
					<FullSizeCircularProgress />
					<Typography variant={'subtitle1'}>이미꽂힌카드</Typography>
				</Stack>
			)} */}
			{!values.failedPaymentRequestData && (
				<Stack
					flex={1}
					width={'100%'}
					alignItems={'center'}
					py={5}
					spacing={5}>
					<Typography
						variant='h6'
						color={'text.secondary'}>
						{t('payment.card.action1')}
						<br />
						{t('payment.card.action2')}
					</Typography>
					<Stack
						width={'100%'}
						height={'100%'}
						justifyContent={'center'}
						alignItems={'center'}>
						<Stack
							width={480}
							height={480}>
							<카드삽입Image />
						</Stack>
					</Stack>
				</Stack>
			)}
			<Stack alignItems={'center'}>
				<Typography
					variant='subtitle1'
					color={'text.secondary'}>
					{!isServerError
						? errors.submit || 'ㅤ'
						: !isRetrying
						? errors.submit || 'ㅤ'
						: t('payment.sending')}
				</Typography>
			</Stack>
			<Stack
				width={'100%'}
				direction={'row'}
				py={3}
				spacing={3}>
				<Button
					variant='outlined'
					fullWidth
					disabled={isSubmitting || isRetrying || safeRunningCheck}
					onClick={goBack}>
					{t('common.return')}
				</Button>
			</Stack>
		</Stack>
	);
};

const 카드삽입Image = styled(Box)(() => ({
	backgroundImage: `url(${icCardImage})`,
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'cover',
	width: '100%',
	height: '100%',
}));

const useRetryFailedPayment = ({
	handleSubmitPayment,
	failedPaymentRequestData,
	handleCompleteRefund중복결제,
}: {
	handleSubmitPayment: (paymentRequestInfo: PaymentRequestInfoType) => Promise<void>;
	failedPaymentRequestData?: PaymentRequestInfoType;
	handleCompleteRefund중복결제: () => void;
}) => {
	const handleRefund중복결제 = PaymentState.actions.refund.useRefundOfficial중복결제();
	const [retry, setRetry] = useState(1);

	useEffect(() => {
		console.log(retry, failedPaymentRequestData);

		(async () => {
			try {
				await sleep(3 * 1000);
				if (failedPaymentRequestData) {
					if (retry < RETRY_LIMIT) {
						// 3번까지 재시도한다.
						await handleSubmitPayment(failedPaymentRequestData);
					} else {
						// 3번까지 재시도하고 안 되면 중복결제를 취소한다.
						const response = await handleRefund중복결제({ paymentInfo: failedPaymentRequestData });
						handleCompleteRefund중복결제();

						setRetry(1);
					}
				}
			} catch (err) {
				Sentry.withScope((scope) => {
					// scope.setTransactionName('키오스크 결제오류');

					scope.setTags({
						Type: '결제',
						Where: 'KIOSK',
					});
					Sentry.captureException(err);
					Sentry.captureMessage(
						`키오스크 결제 실패 (Store ID : ${failedPaymentRequestData?.storeId}) // ` +
							JSON.stringify(failedPaymentRequestData)
					);
				});

				setRetry((prev) => prev + 1);
			}
		})();
	}, [retry, failedPaymentRequestData]);

	const isServerError = failedPaymentRequestData;
	const isRetrying = isServerError && retry < RETRY_LIMIT;

	return { isServerError, isRetrying };
};
